import { useEffect } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import { animeList } from "@/mocks/anime";
import { categories } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "好找",
    text: "题材、地区、年份、评分多种筛选维度组合，减少在海量作品里翻找的时间。",
  },
  {
    icon: "ri-shield-check-line",
    title: "干净",
    text: "以内容浏览为核心，页面结构清晰，不堆叠干扰阅读的元素。",
  },
  {
    icon: "ri-heart-3-line",
    title: "留得住",
    text: "喜欢的作品可以一键收藏，下次打开依然在你的清单里。",
  },
];

const milestones = [
  { year: "2024", title: "站点起步", text: "从零开始整理动漫作品信息结构，先做好分类与详情页。" },
  { year: "2025", title: "筛选升级", text: "上线多条件联动筛选与评分排序，支持按地区与年份快速定位。" },
  { year: "2026", title: "门户成型", text: "完成首页焦点推荐、热门榜单、收藏清单与移动端导航体系。" },
];

export default function About() {
  usePageMeta(
    "关于我们 - 动漫免费在观看",
    "了解动漫免费在观看的定位、使命与联系方式，查看版权声明与免责声明，并通过反馈表单告诉我们需要改进的地方。",
    "关于动漫免费在观看,动漫网站介绍,动漫在线观看,反馈与建议,免责声明",
  );

  useEffect(() => {
    if (window.location.hash === "#feedback") {
      const target = document.getElementById("feedback");
      if (target) {
        window.setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
      }
    }
  }, []);

  return (
    <AppLayout>
      <div className="px-4 md:px-6 pt-5">
        <Breadcrumb items={[{ label: "关于我们" }]} />

        <section className="mt-3 relative overflow-hidden rounded-lg bg-primary-950">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20anime%20style%20landscape%20of%20a%20warm%20orange%20sunset%20sky%20over%20a%20stylized%20city%20skyline%20silhouette%2C%20deep%20navy%20shadows%2C%20soft%20glowing%20particles%2C%20cinematic%20illustration%2C%20no%20text&width=1600&height=600&seq=anime-about-hero-01&orientation=landscape"
            alt="动漫风格城市黄昏插画"
            title="动漫免费在观看 - 关于我们"
            className="absolute inset-0 w-full h-full object-cover object-top opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/25" />
          <div className="relative px-5 py-9 md:px-9 md:py-12 max-w-2xl">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-500 text-primary-950 text-[11px] font-semibold">
              <i className="ri-movie-2-line" />
              ANIME FREE WATCH
            </span>
            <h1 className="mt-3 font-heading text-2xl md:text-3xl font-bold text-background-50 leading-tight">
              关于动漫免费在观看
            </h1>
            <p className="mt-2.5 text-[13px] leading-relaxed text-background-200">
              我们想做的事很简单：让喜欢动漫的人，用最少的步骤找到想看的那一部。
              不堆信息、不做花哨的跳动，把作品摆清楚，把筛选做好用，就够了。
            </p>
          </div>
        </section>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                我们的使命
              </h2>
              <p className="mt-3 text-[13px] leading-[1.9] text-foreground-700">
                动漫是一个信息量极大的领域：每年上线的新作数以百计，题材、地区、制作方、口碑各不相同。
                对普通观众来说，真正困难的往往不是「看什么」，而是「怎么找到想看的」。
                因此我们把精力放在作品信息整理、分类体系与筛选体验上，让浏览本身变得轻松愉快。
              </p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {values.map((item) => (
                  <div key={item.title} className="rounded-md bg-background-50 border border-background-200 px-4 py-4">
                    <span className="w-8 h-8 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center">
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <p className="mt-2.5 font-heading text-[13px] font-semibold text-foreground-900">{item.title}</p>
                    <p className="mt-1 text-[11.5px] leading-relaxed text-foreground-600">{item.text}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                站点发展
              </h2>
              <ol className="mt-4 space-y-4">
                {milestones.map((item, index) => (
                  <li key={item.year} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className="w-2.5 h-2.5 rounded-full bg-accent-500 mt-1.5" />
                      {index < milestones.length - 1 ? <span className="w-px flex-1 bg-background-300 my-1" /> : null}
                    </div>
                    <div className="pb-1">
                      <p className="text-[12px] text-foreground-500">{item.year}</p>
                      <p className="font-heading text-[13.5px] font-semibold text-foreground-900">{item.title}</p>
                      <p className="mt-0.5 text-[12px] leading-relaxed text-foreground-600">{item.text}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            <FeedbackForm />

            <section className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                版权声明与免责声明
              </h2>
              <div className="mt-3 space-y-3 text-[12.5px] leading-[1.9] text-foreground-700">
                <p>
                  <strong className="text-foreground-900">版权声明：</strong>
                  本站所有作品名称、封面插画、简介与集数信息仅用于界面演示与内容展示，相关权利归原作品权利人所有。
                  本站不提供视频文件的上传、存储、播放与下载服务。
                </p>
                <p>
                  <strong className="text-foreground-900">免责声明：</strong>
                  站内所有「播放选集」均为演示界面，点击集数仅切换该集文字简介，不加载、不链接任何视频内容。
                  如需观看正版内容，请前往合法的正版平台。
                </p>
                <p>
                  <strong className="text-foreground-900">数据说明：</strong>
                  收藏记录仅保存在你当前使用的浏览器本地，不会上传服务器；清除浏览器数据后收藏会一并丢失。
                </p>
              </div>
            </section>
          </div>

          <aside className="flex flex-col gap-5">
            <section className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">站点概况</h2>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {[
                  { value: animeList.length, label: "作品", icon: "ri-film-line" },
                  { value: categories.length, label: "题材", icon: "ri-price-tag-3-line" },
                  { value: "5", label: "地区", icon: "ri-earth-line" },
                ].map((stat) => (
                  <div key={stat.label} className="rounded-md bg-background-50 border border-background-200 py-3 text-center">
                    <span className="w-6 h-6 mx-auto flex items-center justify-center text-accent-600">
                      <i className={stat.icon} />
                    </span>
                    <p className="mt-1 font-heading text-[15px] font-bold text-foreground-950">{stat.value}</p>
                    <p className="text-[11px] text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="font-heading text-sm font-bold text-foreground-950">联系我们</h2>
              <ul className="mt-3 space-y-3 text-[12px] text-foreground-700">
                <li className="flex items-start gap-2">
                  <i className="ri-mail-line text-accent-600 mt-0.5" />
                  <span className="break-all">hello@anime-free-watch.demo</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-time-line text-accent-600 mt-0.5" />
                  <span>反馈处理时间：工作日 10:00 - 18:00</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-chat-3-line text-accent-600 mt-0.5" />
                  <span>建议、内容纠错与合作咨询，都可以通过左侧反馈表单提交</span>
                </li>
              </ul>
            </section>

            <section className="rounded-lg bg-primary-950 p-5">
              <h2 className="font-heading text-sm font-bold text-background-50">快速入口</h2>
              <div className="mt-3 grid grid-cols-2 gap-1.5">
                {[
                  { label: "全部筛选", to: "/list", icon: "ri-filter-3-line" },
                  { label: "热门榜单", to: "/list?sort=popular", icon: "ri-fire-line" },
                  { label: "我的收藏", to: "/favorites", icon: "ri-heart-3-line" },
                  { label: "搜索动漫", to: "/search", icon: "ri-search-line" },
                ].map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="flex items-center gap-1.5 px-2.5 py-2 rounded-md bg-primary-800 text-[11.5px] text-background-100 hover:bg-accent-500 hover:text-primary-950 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={link.icon} />
                    {link.label}
                  </Link>
                ))}
              </div>
            </section>
          </aside>
        </div>
      </div>
      <SiteFooter />
    </AppLayout>
  );
}